// src/seo/eventPages.js
//
// Single source of truth for the event-management SEO landing pages
// (/event-management-software and its three focused siblings). Read by:
//   * components/seo/EventSoftwarePage.jsx — the React page.
//   * scripts/buildSeoPages.mjs (postbuild) — writes build/seo/event-pages.json,
//     which server/routes/publicPageRoutes.js uses to send these paths with
//     their own <head> tags, JSON-LD and crawler-readable body HTML, and to
//     list them in /sitemaps/_site.xml.
//
// Must stay plain ESM with no imports: the build script loads it in Node.
//
// Every feature claim maps to a section of the user guide (/docs) — keep it
// that way. If a feature changes, edit it here; nothing else holds copy.

export const SITE = 'https://on-screen.app';
export const OG_IMAGE = `${SITE}/on-screen-logo-white-bezel-fullres.png`;
export const SIGNUP_URL = '/owneradd';
export const DOCS_URL = '/docs/index.html';

const DOC = (file) => `/docs/${file}`;

// Shared blocks -------------------------------------------------------------

const STEP_CREATE = {
  title: 'Create the event',
  body: 'Add the event with its date, venue, and description, then add items to it. An item can be a ticketed event, an auction, a raffle, a contest, a poll, or another event action — so one event can hold a show, a silent auction, and a raffle at the same time.',
  doc: DOC('OnScreen_11_Events.html'),
};

const STEP_REGISTER = {
  title: 'Open registration',
  body: 'Collect name, email, and phone, plus any custom questions you need — dietary restrictions, T-shirt size, emergency contact — each marked required or optional.',
  doc: DOC('OnScreen_20_Ticketing_Check_in.html'),
};

const STEP_TICKETS = {
  title: 'Sell tickets',
  body: 'Set up priced ticket tiers with their own capacity, add promo codes (fixed amount or percent off, with an optional redemption cap), and let buyers join a waitlist when a tier sells out. Payments go to the Stripe or Square account you connect.',
  doc: DOC('OnScreen_20_Ticketing_Check_in.html'),
};

const STEP_PROMOTE = {
  title: 'Promote it',
  body: 'Share the public event page by link or QR code. A marketing campaign builds a promotion timeline backward from the event date and schedules posts to your connected social accounts. The ticketing dashboard shows where buyers came from.',
  doc: DOC('OnScreen_28_Marketing_Campaigns.html'),
};

const STEP_ATTENDEES = {
  title: 'Manage attendees',
  body: 'See every guest in one filterable roster. Buyers get a ticket wallet with one QR code per ticket and can transfer a ticket to someone else. Send complimentary tickets straight to an email address — nothing is usable until the recipient confirms the claim link.',
  doc: DOC('OnScreen_30_Public_Event_Attendee_Pages.html'),
};

const STEP_CHECKIN = {
  title: 'Check guests in with QR codes',
  body: 'Staff scan tickets with a phone or tablet camera from a mobile-first door console. Use the manual override for anyone the scanner can’t read, and register walk-ins on the spot — comped or charged a tier.',
  doc: DOC('OnScreen_20_Ticketing_Check_in.html'),
};

const STEP_AFTER = {
  title: 'Wrap up and report',
  body: 'The Event Hub shows revenue, tickets sold, and check-in progress alongside orders, vendors, and budget. Afterward, publish a public recap page with the photos and clips you choose — even ones collected from a photographer through a no-account upload link.',
  doc: DOC('OnScreen_29_The_Event_Hub.html'),
};

const FAQ_ACCOUNT = {
  q: 'Do attendees need an account to register or buy tickets?',
  a: 'No. The public event page, registration form, and checkout work without a login. Buyers open their tickets from the confirmation email.',
};

const FAQ_DEVICES = {
  q: 'What do I need to run check-in at the door?',
  a: 'A phone, tablet, or laptop with a camera. On-Screen installs as a PWA on iOS, Android, Windows, and macOS — no app store needed.',
};

const FAQ_MODULAR = {
  q: 'Do I have to use the whole platform?',
  a: 'No. Use only what you need — ticketing and check-in, for example — and add membership, social scheduling, or auctions and raffles later.',
};

const FAQ_PAYMENTS = {
  q: 'Where does ticket revenue go?',
  a: 'To the Stripe or Square account you connect in settings, so charges settle directly to your organization.',
};

const FAQ_START = {
  q: 'How do I get started?',
  a: 'Create a free account, add your organization, and create your first event. The user guide walks through each step with screenshots.',
};

// Pages ---------------------------------------------------------------------

export const EVENT_PAGES = [
  {
    slug: 'event-management-software',
    navLabel: 'Event management',
    relatedTitle: 'Event management software',
    title: 'Event Management Software for Small Organizations | On-Screen',
    description:
      'Create events, collect registrations, sell tickets, check guests in with QR codes, and promote it all on social media. One platform for small businesses, nonprofits, and community organizations.',
    eyebrow: 'Event management',
    h1: 'Event Management Software for Small Businesses, Nonprofits, and Community Organizations',
    intro:
      'Plan the event, sell the tickets, run the door, and promote it — from one dashboard instead of a separate tool for each step.',
    workflowTitle: 'How an event runs in On-Screen',
    workflow: [STEP_CREATE, STEP_REGISTER, STEP_TICKETS, STEP_PROMOTE, STEP_ATTENDEES, STEP_CHECKIN, STEP_AFTER],
    highlights: {
      title: 'More than a ticket form',
      items: [
        { title: 'Auctions, raffles, and contests', body: 'Run them as items inside the same event, with entries, bids, and winners in one results view.', doc: DOC('OnScreen_26_Event_Actions_Setup_Results.html') },
        { title: 'Team roles and tasks', body: 'Give staff admin, manager, or operator access and assign event tasks with due dates.', doc: DOC('OnScreen_27_My_Tasks.html') },
        { title: 'Membership directory', body: 'Keep donors, volunteers, repeat customers, and ticket holders in one place, with a member portal for their tickets and event history.', doc: DOC('OnScreen_12_Membership.html') },
        { title: 'Social scheduling', body: 'Schedule posts, answer comments and DMs from one inbox, and see engagement analytics.', doc: DOC('OnScreen_22_Calendar_Scheduled_Posts.html') },
      ],
    },
    faq: [FAQ_ACCOUNT, FAQ_DEVICES, FAQ_MODULAR, FAQ_PAYMENTS, FAQ_START],
    related: ['event-registration-software', 'event-ticketing-software', 'nonprofit-event-management-software'],
  },

  {
    slug: 'event-registration-software',
    navLabel: 'Registration',
    relatedTitle: 'Event registration software',
    title: 'Event Registration Software with Custom Forms and QR Check-In | On-Screen',
    description:
      'Online event registration with custom questions, waitlists, QR code tickets, walk-in registration, and a live guest roster. No attendee login required.',
    eyebrow: 'Event registration',
    h1: 'Event Registration Software for Small Organizations',
    intro:
      'Share one link, collect exactly the information you need, and walk into the event with a roster that’s already sorted.',
    workflowTitle: 'From sign-up to the door',
    workflow: [
      { title: 'Publish the event page', body: 'Your public event page shows the hero image, date, venue, and description. Share it by link or QR code on social media, your website, or a printed flyer.', doc: DOC('OnScreen_30_Public_Event_Attendee_Pages.html') },
      STEP_REGISTER,
      { title: 'Handle capacity', body: 'Each ticket tier has its own capacity. When a tier fills up, registrants can join the waitlist instead of leaving.', doc: DOC('OnScreen_30_Public_Event_Attendee_Pages.html') },
      { title: 'Confirm by email', body: 'Registrants get a confirmation email that opens their ticket wallet — one QR code per ticket, transferable to someone else.', doc: DOC('OnScreen_30_Public_Event_Attendee_Pages.html') },
      { title: 'Work from the roster', body: 'Every registrant and their answers in one filterable list, with check-in status updating live at the door.', doc: DOC('OnScreen_20_Ticketing_Check_in.html') },
      { ...STEP_CHECKIN, title: 'Check in and register walk-ins' },
    ],
    highlights: {
      title: 'Registration details that matter',
      items: [
        { title: 'Custom fields', body: 'Required or optional questions beyond name, email, and phone.' },
        { title: 'No attendee accounts', body: 'People register from the public page without creating a login.' },
        { title: 'Comp registrations', body: 'Send a free ticket to a speaker, sponsor, or volunteer by email.' },
        { title: 'Member portal', body: 'Members can sign in to see their own tickets and event history.', doc: DOC('OnScreen_32_The_Member_Portal.html') },
      ],
    },
    faq: [
      FAQ_ACCOUNT,
      { q: 'Can I ask custom questions during registration?', a: 'Yes. Add any number of custom fields and mark each one required or optional.' },
      { q: 'What happens when the event is full?', a: 'Registrants can join the waitlist for a sold-out tier from the same page.' },
      { q: 'Can I register people at the door?', a: 'Yes. Staff can add walk-ins from the check-in console with their name, contact info, tier, and ticket count.' },
      FAQ_START,
    ],
    related: ['event-management-software', 'event-ticketing-software', 'nonprofit-event-management-software'],
  },

  {
    slug: 'event-ticketing-software',
    navLabel: 'Ticketing',
    relatedTitle: 'Event ticketing software',
    title: 'Event Ticketing Software for Small Organizations | On-Screen',
    description:
      'Sell event tickets with priced tiers, capacity limits, promo codes, and waitlists. QR code tickets, camera check-in, and a revenue dashboard. Payments go to your own Stripe or Square account.',
    eyebrow: 'Event ticketing',
    h1: 'Event Ticketing Software for Small Organizations',
    intro:
      'Set up tiers, share the link, and watch sales and check-ins in one dashboard — with revenue settling to your own payment account.',
    workflowTitle: 'How ticketing works',
    workflow: [
      { title: 'Turn on ticketing', body: 'Enable ticketing on any event item. Shows, sessions, product releases, demos, and fundraisers all work the same way.', doc: DOC('OnScreen_20_Ticketing_Check_in.html') },
      { title: 'Add ticket tiers', body: 'General admission, VIP, early access — each tier with its own price and capacity, and a live sales count.', doc: DOC('OnScreen_20_Ticketing_Check_in.html') },
      { title: 'Add promo codes', body: 'Fixed-amount or percentage discounts with an optional cap on redemptions.', doc: DOC('OnScreen_20_Ticketing_Check_in.html') },
      { title: 'Share the purchase page', body: 'Buyers pick a tier and quantity, answer your registration questions, and pay. No account needed.', doc: DOC('OnScreen_30_Public_Event_Attendee_Pages.html') },
      { title: 'Deliver QR tickets', body: 'Each ticket gets its own QR code in the buyer’s ticket wallet, and tickets can be transferred to another person.', doc: DOC('OnScreen_30_Public_Event_Attendee_Pages.html') },
      { ...STEP_CHECKIN, title: 'Scan at the door' },
      { title: 'Track revenue and attribution', body: 'Tickets sold per tier, total revenue, and where buyers came from — in one dashboard and in the Event Hub on the day.', doc: DOC('OnScreen_29_The_Event_Hub.html') },
    ],
    highlights: {
      title: 'Built for the night of the event',
      items: [
        { title: 'Your payment account', body: 'Connect Stripe or Square so charges settle directly to you.', doc: DOC('OnScreen_25_Settings_Detail.html') },
        { title: 'Waitlist', body: 'Sold-out tiers collect a waitlist instead of turning people away.' },
        { title: 'Comp tickets', body: 'Email a free ticket; it only activates after the recipient confirms.' },
        { title: 'Walk-in sales', body: 'Charge or comp a tier at the door from the check-in console.' },
      ],
    },
    faq: [
      FAQ_PAYMENTS,
      { q: 'Can I offer discounts?', a: 'Yes. Promo codes can take a fixed amount or a percentage off, with an optional limit on how many times each code is used.' },
      { q: 'Can a buyer give their ticket to someone else?', a: 'Yes. Tickets can be transferred from the buyer’s ticket wallet.' },
      FAQ_DEVICES,
      FAQ_START,
    ],
    related: ['event-management-software', 'event-registration-software', 'nonprofit-event-management-software'],
  },

  {
    slug: 'nonprofit-event-management-software',
    navLabel: 'Nonprofits',
    relatedTitle: 'Nonprofit event management',
    title: 'Nonprofit Event Management Software for Fundraisers and Galas | On-Screen',
    description:
      'Run fundraising events with ticketing, silent auctions, raffles, donor and volunteer records, QR check-in, and social promotion — in one affordable platform for nonprofits.',
    eyebrow: 'For nonprofits',
    h1: 'Event Management Software for Nonprofits',
    intro:
      'Galas, benefit shows, auctions, and raffles take a lot of moving parts. Keep tickets, bidding, supporters, volunteers, and promotion in one place.',
    workflowTitle: 'A fundraiser, start to finish',
    workflow: [
      { ...STEP_CREATE, title: 'Build the fundraiser', body: 'Create the event and add items for each part of it — admission tickets, silent auction lots, a raffle, a contest.' },
      { title: 'Sell tickets and sponsorship tiers', body: 'Priced tiers with capacity, promo codes for board members or partners, and complimentary tickets for sponsors and volunteers sent by email.', doc: DOC('OnScreen_20_Ticketing_Check_in.html') },
      { title: 'Run auctions and raffles', body: 'Supporters place bids and enter raffles online. Entries and bids land in one results view, where winners are drawn or awarded.', doc: DOC('OnScreen_26_Event_Actions_Setup_Results.html') },
      { title: 'Coordinate volunteers and staff', body: 'Give team members role-based access, assign tasks with due dates, and message them inside the platform.', doc: DOC('OnScreen_17_Team_Collaboration.html') },
      { title: 'Promote with a campaign', body: 'Set goals, and On-Screen plans social posts backward from the event date. Results roll up as goal versus actual.', doc: DOC('OnScreen_28_Marketing_Campaigns.html') },
      STEP_CHECKIN,
      { title: 'Report back to supporters', body: 'Track revenue, vendors, and budget in the Event Hub, then publish a recap page with photos so donors can see what their support made possible.', doc: DOC('OnScreen_34_Event_History_Article.html') },
    ],
    highlights: {
      title: 'Why nonprofits use one platform',
      items: [
        { title: 'Donor and volunteer records', body: 'Members get a profile and a public membership card.', doc: DOC('OnScreen_12_Membership.html') },
        { title: 'Event history', body: 'Supporters can browse photos and clips from past events and comment on them.', doc: DOC('OnScreen_18_Member_Event_History.html') },
        { title: 'Fewer subscriptions', body: 'Ticketing, auctions, raffles, membership, and social media without stitching tools together.' },
        { title: 'Pick what you need', body: 'Start with ticketing or auctions and add the rest later.' },
      ],
    },
    faq: [
      { q: 'Can I run a silent auction and sell tickets for the same event?', a: 'Yes. Each event holds multiple items, so admission tickets, auction lots, and a raffle can live under one event.' },
      { q: 'Are raffles allowed for our organization?', a: 'Raffle and gaming rules vary by state and country, and many require a license or permit. Check with your state’s gambling control or charitable gaming office before running one.' },
      { q: 'Can volunteers help run check-in?', a: 'Yes. Add them as team users with an operator role and they can scan tickets from their own phone.' },
      FAQ_PAYMENTS,
      FAQ_START,
    ],
    related: ['event-management-software', 'event-ticketing-software', 'event-registration-software'],
  },
];

export const pageBySlug = (slug) => EVENT_PAGES.find((p) => p.slug === slug);

export const pageUrl = (page) => `${SITE}/${page.slug}`;

// Structured data -----------------------------------------------------------

export function buildJsonLd(page) {
  const url = pageUrl(page);
  return [
    {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'On-Screen',
      url,
      applicationCategory: 'BusinessApplication',
      applicationSubCategory: 'Event management software',
      operatingSystem: 'Web, iOS, Android, Windows, macOS',
      description: page.description,
      publisher: { '@type': 'Organization', name: 'SE3D Inc.' },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: page.faq.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'On-Screen', item: `${SITE}/about` },
        { '@type': 'ListItem', position: 2, name: page.navLabel, item: url },
      ],
    },
  ];
}
